import * as React from 'react';
import { PERMISSIONS } from './permissions';
import { OffersIcon } from '../icons/OffersIcon';
import { LogsIcon } from '../icons/LogsIcon';
import { IntegrationsIcon } from '../icons/IntegrationsIcon';
import { PagesIcon } from '../icons/PagesIcon';
import { InsuranceIcon } from '../icons/InsuranceIcon';
import { ShortLinkIcon } from '../icons/ShortLinkIcon';
import { ManageCustomersIcon } from '../icons/ManageCustomersIcon';
import { DMSSettingsIcon } from '../icons/DMSSettingsIcon';
import { PurchasingIcon } from '../icons/PurchasingIcon';
import { SallaIcon } from '../icons/SallaIcon';
import { PermissionsIcon } from '../icons/PermissionsIcon';
import { ReportIcon } from '../icons/ReportIcon';
import { OrderFulfilmentIcon } from '../icons/OrderFulfilmentIcon';
import { PharmacistWorkspaceIcon } from '../icons/PharmacistWorkspaceIcon';
import { COMPANY_MODELS, hasCompanyModel } from '../Hooks/company';

export interface MenuItem {
  key: string;
  title: string;
  path?: string;
  icon?: React.ComponentType<any>;
  permissions?: string[];
  /** When true, all listed permissions are required (AND). Default is OR. */
  requireAllPermissions?: boolean;
  children?: MenuItem[];
  isVisible?: boolean;
  userTypes?: number[];
}

export const MENU_CONFIG: MenuItem[] = [

  {
    key: 'home',
    title: 'sideBar.home',
    path: '/',
  },
  {
    key: 'settings',
    title: 'sideBar.Setting',
    children: [
        {
          key: 'all-companies',
          title: 'sideBar.setting.all-companies',
          path: '/all-companies',
          permissions: [PERMISSIONS.COMPANY.VIEW],
          userTypes: [0],
        },
        {
          key: 'company-customizations',
          title: 'sideBar.setting.company-customizations',
          path: '/company-customizations',
          permissions: [PERMISSIONS.COMPANY.VIEW],
          userTypes: [0],
        },
        {
          key: 'company-setting',
          title: 'sideBar.setting.company-setting',
          path: '/company-setting',
          permissions: [PERMISSIONS.COMPANY.SETTINGS],
        },
        {
          key: 'users',
          title: 'sideBar.users',
          path: '/users',
          permissions: [PERMISSIONS.USER.VIEW],
        },
        {
          key: 'notification-sounds',
          title: 'sideBar.setting.notification-sounds',
          path: '/notification-sounds',
          permissions: [PERMISSIONS.NOTIFICATION_SOUNDS.VIEW],
        },
        {
          key: 'payment-settings',
          title: 'sideBar.setting.payment-settings',
          path: '/payment-settings',
          permissions: [PERMISSIONS.PAYMENT_SETTINGS.VIEW],
        },
        {
          key: 'customer-segmentation',
          title: 'sideBar.setting.customer-segmentation',
          path: '/customer-segmentation',
          permissions: [PERMISSIONS.CUSTOMER_SEGMENTATION.VIEW],
        },
        {
          key: 'export-reports',
          title: 'sideBar.setting.export-reports',
          path: '/company-settings/export-reports',
          permissions: [PERMISSIONS.COMPANY.SETTINGS, PERMISSIONS.REPORTS.EXPORT],
          requireAllPermissions: true,
        },
        {
          key: 'email-settings',
          title: 'sideBar.setting.email-settings',
          path: '/email-settings',
          permissions: [PERMISSIONS.EMAIL_SETTINGS.VIEW],
        },
        {
          key: 'push-notifications',
          title: 'sideBar.setting.push-notifications',
          path: '/company-settings/push-notifications',
          permissions: [PERMISSIONS.PUSH_NOTIFICATIONS.VIEW, PERMISSIONS.PUSH_NOTIFICATIONS.SEND],
        },
        {
          key: 'organization',
          title: 'sideBar.setting.organization',
          path: '/settings/organization',
          permissions: [PERMISSIONS.ORGANIZATION.VIEW],
        },
        {
          key: 'sms-templates',
          title: 'sideBar.setting.sms-templates',
          path: '/settings/sms-templates',
          permissions: [PERMISSIONS.SMS_TEMPLATE.VIEW],
        },
        {
          key: 'order-receipt',
          title: 'sideBar.setting.order-receipt',
          path: '/settings/order-receipt',
          permissions: [
            PERMISSIONS.ORDER_RECEIPT_SETTINGS.VIEW,
            PERMISSIONS.ORDER_RECEIPT_SETTINGS.EDIT,
          ],
        },
      ],
  },
  {
    key: 'permissions',
    title: 'sideBar.permissions',
    userTypes: [0, 1],
    icon: PermissionsIcon,
    permissions: [
      PERMISSIONS.ROLE.VIEW,
      PERMISSIONS.PERMISSION.VIEW,
      PERMISSIONS.MODEL.VIEW,
    ],
    children: [
      {
        key: 'permissions-models',
        title: 'sideBar.permissionsModels',
        path: '/permissions?tab=models',
        userTypes: [0],
        permissions: [PERMISSIONS.MODEL.VIEW],
      },
      {
        key: 'permissions-permissions',
        title: 'sideBar.permissionsPermissions',
        path: '/permissions?tab=permissions',
        userTypes: [0],
        permissions: [PERMISSIONS.PERMISSION.VIEW],
      },
      {
        key: 'permissions-roles',
        title: 'sideBar.permissionsRoles',
        path: '/permissions?tab=roles',
        userTypes: [0, 1],
        permissions: [PERMISSIONS.ROLE.VIEW],
      },
    ],
  },
  {
    key: 'orders',
    title: 'sideBar.orders',
    children: [
      {
        key: 'addOrder',
        title: 'sideBar.addOrder',
        path: '/orders/add',
        permissions: [PERMISSIONS.ORDER.CREATE],
      },
      {
        key: 'orderList',
        title: 'sideBar.orderList',
        path: '/orders/list',
        permissions: [PERMISSIONS.ORDER.VIEW],
      },
      {
        key: 'activeOrders',
        title: 'sideBar.activeOrders',
        path: '/orders/active',
        permissions: [PERMISSIONS.ORDER.VIEW],
      },
      {
        key: 'orderTracking',
        title: 'sideBar.orderTracking',
        path: '/orders/tracking',
        permissions: [PERMISSIONS.ORDER.MAP_TRACKING],
      },
      {
        key: 'insurance-order',
        title: 'sideBar.insuranceOrder',
        path: '/insurance-order',
        permissions: [PERMISSIONS.INSURANCE_ORDER.VIEW],
      },
      {
        key: 'orderFulfilment',
        title: 'sideBar.orderFulfilment',
        path: '/orders/fulfilment',
        icon: OrderFulfilmentIcon,
        permissions: [PERMISSIONS.ORDER_FULFILMENT.VIEW],
      },
      {
        key: 'pharmacistWorkspace',
        title: 'sideBar.pharmacistWorkspace',
        path: '/orders/pharmacist-review',
        icon: PharmacistWorkspaceIcon,
        permissions: [
          PERMISSIONS.PRESCRIPTION_REVIEW.VIEW,
          PERMISSIONS.PRESCRIPTION_REVIEW.REVIEW,
        ],
      },
      {
        key: 'refunds',
        title: 'sideBar.refunds',
        path: '/orders/refunds',
        permissions: [PERMISSIONS.REFUND.VIEW],
      },
      {
        key: 'refundApprovalRules',
        title: 'sideBar.refundApprovalRules',
        path: '/orders/refund-approval-rules',
        permissions: [PERMISSIONS.REFUND_APPROVAL_RULE.VIEW],
      },
    ],
  },
  {
    key: 'activities',
    title: 'sideBar.activities',
    children: [
      {
        key: 'allTickets',
        title: 'sideBar.allTickets',
        path: '/all-activities',
        permissions: [PERMISSIONS.ACTIVITY.VIEW],
      },
      {
        key: 'activityTypes',
        title: 'sideBar.activity-types',
        path: '/activity-management?tab=types',
        permissions: [PERMISSIONS.ACTIVITY_TYPE.VIEW],
      },
      {
        key: 'activitySections',
        title: 'sideBar.activity-sections',
        path: '/activity-management?tab=sections',
        permissions: [PERMISSIONS.ACTIVITY_TYPE.VIEW],
      },
      {
        key: 'activityReasons',
        title: 'sideBar.activity-reasons',
        path: '/activity-management?tab=reasons',
        permissions: [PERMISSIONS.ACTIVITY_TYPE.VIEW],
      },
      {
        key: 'activityStatus',
        title: 'sideBar.activity-status',
        path: '/activity-management?tab=status',
        permissions: [PERMISSIONS.ACTIVITY_STATUS.VIEW],
      },
      {
        key: 'activityPriorities',
        title: 'sideBar.activityPriorities',
        path: '/activity-management?tab=priorities',
        permissions: [PERMISSIONS.ACTIVITY_PRIORITY.VIEW],
      },
    ],
  },
  {
    key: 'catalog',
    title: 'sideBar.catalog',
    children: [
      {
        key: 'product',
        title: 'sideBar.product',
        path: '/catalog/product',
        permissions: [PERMISSIONS.PRODUCT.VIEW],
      },
      {
        key: 'categories',
        title: 'sideBar.categories',
        path: '/catalog/categories',
        permissions: [PERMISSIONS.CATEGORY.VIEW],
      },
      {
        key: 'brands',
        title: 'sideBar.brands',
        path: '/catalog/brands',
        permissions: [PERMISSIONS.BRAND.VIEW],
      },
      {
        key: 'manufacturers',
        title: 'sideBar.manufacturers',
        path: '/catalog/manufacturers',
        permissions: [PERMISSIONS.MANUFACTURER.VIEW],
      },
      {
        key: 'extra',
        title: 'sideBar.extra',
        path: '/catalog/extra',
        permissions: [PERMISSIONS.EXTRA.VIEW],
      },
      {
        key: 'productAttributes',
        title: 'sideBar.productAttributes',
        path: '/catalog/product-attributes',
        permissions: [PERMISSIONS.PRODUCT_ATTRIBUTE.VIEW],
      },
      {
        key: 'keywords',
        title: 'sideBar.keywords',
        path: '/catalog/keywords',
        permissions: [PERMISSIONS.KEYWORD.VIEW],
      },
      {
        key: 'productReviews',
        title: 'sideBar.productReviews',
        path: '/catalog/product-reviews',
        permissions: [PERMISSIONS.PRODUCT_REVIEW.VIEW],
      },
      {
        key: 'reviewBannedWords',
        title: 'sideBar.reviewBannedWords',
        path: '/catalog/review-banned-words',
        permissions: [PERMISSIONS.PRODUCT_REVIEW.VIEW],
      },
      {
        key: 'fileManager',
        title: 'sideBar.fileManager',
        path: '/catalog/file-manager',
        permissions: [PERMISSIONS.FILE_MANAGER.VIEW],
      },
      {
        key: 'suppliers',
        title: 'sideBar.suppliers',
        path: '/suppliers',
        permissions: [PERMISSIONS.SUPPLIER.VIEW],
      },
    ],
  },
  {
    key: 'salla-orders',
    title: 'sideBar.sallaOrders',
    icon: SallaIcon,
    permissions: [
      PERMISSIONS.SALLA_ORDERS.VIEW,
      PERMISSIONS.SALLA_ORDERS.ALL,
      PERMISSIONS.SALLA_ORDERS.PURCHASED_VIEW,
    ],
    children: [
      {
        key: 'salla-orders-all',
        title: 'sideBar.sallaAllOrders',
        path: '/salla-orders',
        permissions: [
          PERMISSIONS.SALLA_ORDERS.VIEW,
          PERMISSIONS.SALLA_ORDERS.ALL,
        ],
      },
      {
        key: 'salla-orders-purchased',
        title: 'sideBar.sallaPurchasedOrders',
        path: '/salla-orders/purchased',
        permissions: [
          PERMISSIONS.SALLA_ORDERS.VIEW,
          PERMISSIONS.SALLA_ORDERS.PURCHASED_VIEW,
        ],
      },
    ],
  },
  {
    key: 'reports',
    title: 'sideBar.reports',
    icon: ReportIcon,
    children: [
      {
        key: 'reports-main',
        title: 'sideBar.reports',
        path: '/reports',
        // Restricted: requires Company Settings in addition to report_view
        permissions: [PERMISSIONS.REPORTS.VIEW, PERMISSIONS.COMPANY.SETTINGS],
        requireAllPermissions: true,
      },
      {
        key: 'reports-dashboard',
        title: 'sideBar.reportsDashboard',
        path: '/reports/dashboard',
        // Allowed without Company Settings
        permissions: [PERMISSIONS.REPORTS.VIEW],
      },
      {
        key: 'reports-export-logs',
        title: 'sideBar.reportsExportLogs',
        path: '/reports/export-logs',
        // Allowed without Company Settings (export permission still required)
        permissions: [PERMISSIONS.REPORTS.VIEW, PERMISSIONS.REPORTS.EXPORT],
        requireAllPermissions: true,
      },
      {
        key: 'reports-companies',
        title: 'sideBar.reportsCompanies',
        path: '/reports/companies',
        // Allowed without Company Settings (Awfar admin only)
        permissions: [PERMISSIONS.REPORTS.VIEW],
        userTypes: [0],
      },
      {
        key: 'reports-store-compare',
        title: 'sideBar.reportsStoreCompare',
        path: '/reports/store-compare',
        permissions: [PERMISSIONS.REPORTS.VIEW, PERMISSIONS.COMPANY.SETTINGS],
        requireAllPermissions: true,
      },
      {
        key: 'reports-growth',
        title: 'sideBar.reportsGrowth',
        path: '/reports/growth',
        permissions: [PERMISSIONS.REPORTS.VIEW, PERMISSIONS.COMPANY.SETTINGS],
        requireAllPermissions: true,
      },
      {
        key: 'reports-products',
        title: 'sideBar.reportsProducts',
        path: '/reports/products',
        permissions: [PERMISSIONS.REPORTS.VIEW, PERMISSIONS.COMPANY.SETTINGS],
        requireAllPermissions: true,
      },
      {
        key: 'reports-customers',
        title: 'sideBar.reportsCustomers',
        path: '/reports/customers',
        permissions: [PERMISSIONS.REPORTS.VIEW, PERMISSIONS.COMPANY.SETTINGS],
        requireAllPermissions: true,
      },
      {
        key: 'reports-order-analytics',
        title: 'sideBar.reportsOrderAnalytics',
        path: '/reports/order-analytics',
        permissions: [PERMISSIONS.REPORTS.VIEW, PERMISSIONS.COMPANY.SETTINGS],
        requireAllPermissions: true,
      },
      {
        key: 'reports-revenue',
        title: 'sideBar.reportsRevenue',
        path: '/reports/revenue',
        permissions: [PERMISSIONS.REPORTS.VIEW, PERMISSIONS.COMPANY.SETTINGS],
        requireAllPermissions: true,
      },
    ],
  },
  {
    key: 'insurance',
    title: 'sideBar.insurance',
    icon: InsuranceIcon,
    children: [
      {
        key: 'insurance-companies',
        title: 'sideBar.insuranceCompanies',
        path: '/insurance-companies',
        permissions: [PERMISSIONS.INSURANCE.VIEW],
      },
    ],
  },
  {
    key: 'short-links',
    title: 'sideBar.shortLinks',
    path: '/short-links',
    icon: ShortLinkIcon,
    permissions: [PERMISSIONS.SHORT_LINK.VIEW],
  },
  {
    key: 'pages',
    title: 'sideBar.pages',
    icon: PagesIcon,
    permissions: [PERMISSIONS.PAGES.VIEW],
    children: [
      {
        key: 'about',
        title: 'sideBar.aboutPage',
        path: '/pages/about',
        permissions: [PERMISSIONS.PAGES.VIEW],
      },
      {
        key: 'privacy',
        title: 'sideBar.privacyPage',
        path: '/pages/privacy',
        permissions: [PERMISSIONS.PAGES.VIEW],
      },
      {
        key: 'terms',
        title: 'sideBar.termsPage',
        path: '/pages/terms',
        permissions: [PERMISSIONS.PAGES.VIEW],
      },
      {
        key: 'faq',
        title: 'sideBar.faqPage',
        path: '/pages/faq',
        permissions: [PERMISSIONS.PAGES.VIEW],
      },
    ],
  },
  {
    key: 'offers',
    title: 'sideBar.offers',
    icon: OffersIcon,
    children: [
      {
        key: 'all-offers',
        title: 'sideBar.allOffers',
        path: '/offers',
        permissions: [PERMISSIONS.OFFER.VIEW],
      },
      {
        key: 'offers-group',
        title: 'sideBar.offersGroup',
        path: '/offers-group',
        permissions: [PERMISSIONS.OFFER.VIEW],
      },
    ],
  },
  {
    key: 'eCommerce',
    title: 'sideBar.eCommerce',
    children: [
      {
        key: 'slider',
        title: 'sideBar.slider',
        path: '/e-commerce/sliders',
        permissions: [PERMISSIONS.SLIDER.VIEW],
      },
      {
        key: 'cancelReasons',
        title: 'sideBar.cancelReasons',
        path: '/e-commerce/cancel-reasons',
        permissions: [PERMISSIONS.CANCEL_REASON.VIEW],
      },
      {
        key: 'uncompletedOrderReasons',
        title: 'sideBar.uncompletedOrderReasons',
        path: '/e-commerce/uncompleted-order-reasons',
        permissions: [
          PERMISSIONS.UNCOMPLETED_ORDER_REASON.VIEW,
          'manage_uncompleted_order_reason_view',
          'uncompleted_order_reason_view',
        ],
      },
      {
        key: 'prescriptionStatuses',
        title: 'sideBar.prescriptionStatuses',
        path: '/e-commerce/prescription-statuses',
        permissions: [PERMISSIONS.PRESCRIPTION_STATUS.VIEW],
      },
      {
        key: 'prescriptionRejectReasons',
        title: 'sideBar.prescriptionRejectReasons',
        path: '/e-commerce/prescription-reject-reasons',
        permissions: [PERMISSIONS.PRESCRIPTION_REJECT_REASON.VIEW],
      },
      {
        key: 'sections',
        title: 'sideBar.sections',
        path: '/e-commerce/sections',
        permissions: [PERMISSIONS.SECTION.VIEW],
      },
      {
        key: 'flashDeal',
        title: 'sideBar.flashDeal',
        path: '/e-commerce/flash-deal',
        permissions: [PERMISSIONS.FLASH_DEAL.SHOW, PERMISSIONS.FLASH_DEAL.VIEW],
      },
      {
        key: 'promocode',
        title: 'sideBar.promocode',
        path: '/e-commerce/promocodes',
        permissions: [PERMISSIONS.PROMOCODE.VIEW],
      },
      {
        key: 'homeStaticContent',
        title: 'sideBar.homeStaticContent',
        path: '/e-commerce/home-static-content',
        permissions: [PERMISSIONS.HOME_STATIC_CONTENT.VIEW],
      },
      {
        key: 'homeLayout',
        title: 'sideBar.homeLayout',
        path: '/e-commerce/home-layout',
        permissions: [PERMISSIONS.SECTION.VIEW],
      },
      {
        key: 'blogs',
        title: 'sideBar.blogs',
        path: '/e-commerce/blogs',
        permissions: [PERMISSIONS.SECTION.VIEW],
      },
      {
        key: 'videos',
        title: 'sideBar.videos',
        path: '/e-commerce/videos',
        permissions: [PERMISSIONS.SECTION.VIEW],
      },
      {
        key: 'symptoms',
        title: 'sideBar.symptoms',
        path: '/e-commerce/symptoms',
        permissions: [PERMISSIONS.SECTION.VIEW],
      },
    ],
  },
  {
    key: 'manage-customers',
    title: 'sideBar.manageCustomers',
    path: '/manage-customers',
    icon: ManageCustomersIcon,
    permissions: [PERMISSIONS.CUSTOMER.MANAGE],
  },
  {
    key: 'locations',
    title: 'sideBar.locations',
    children: [
      {
        key: 'countries',
        title: 'sideBar.countries',
        path: '/locations/countries',
        permissions: [PERMISSIONS.COUNTRY.VIEW],
        userTypes: [0],
      },
      {
        key: 'cities',
        title: 'sideBar.cities',
        path: '/locations/cities',
        permissions: [PERMISSIONS.CITY.VIEW],
      },
      {
        key: 'areas',
        title: 'sideBar.areas',
        path: '/locations/areas',
        permissions: [PERMISSIONS.AREA.VIEW],
      },
      {
        key: 'regions',
        title: 'sideBar.regions',
        path: '/locations/regions',
        permissions: [PERMISSIONS.REGION.VIEW],
      },
    ],
  },
  {
    key: 'logs',
    title: 'sideBar.logs',
    icon: LogsIcon,
    children: [
      {
        key: 'failedProductImports',
        title: 'sideBar.failedProductImports',
        path: '/logs/failed-product-imports',
        permissions: [PERMISSIONS.PRODUCT.VIEW],
      },
      {
        key: 'failedOfferImports',
        title: 'sideBar.failedOfferImports',
        path: '/logs/failed-offer-imports',
        permissions: [PERMISSIONS.OFFER.VIEW],
      },
      {
        key: 'failedCategoryImports',
        title: 'sideBar.failedCategoryImports',
        path: '/logs/failed-category-imports',
        permissions: [PERMISSIONS.CATEGORY.VIEW],
      },
      {
        key: 'integrationMonitoring',
        title: 'sideBar.integrationMonitoring',
        path: '/logs/integration-monitoring',
        permissions: [PERMISSIONS.REPORTS.VIEW],
        // Payloads can carry personal data, so only Awfar (0) and company (1) admins.
        userTypes: [0, 1],
      },
    ],
  },
  {
    key: 'integrations',
    title: 'sideBar.integrations',
    icon: IntegrationsIcon,
    children: [
      {
        key: 'sms',
        title: 'sideBar.sms',
        path: '/integrations/sms',
        permissions: [PERMISSIONS.COMPANY_CONFIGURATION.VIEW],
      },
      {
        key: 'paymentGateway',
        title: 'sideBar.paymentGateway',
        path: '/integrations/payment-gateway',
        permissions: [PERMISSIONS.COMPANY_CONFIGURATION.VIEW],
      },
    ],
  },
  {
    key: 'dms-settings',
    title: 'sideBar.dmsSettings',
    icon: DMSSettingsIcon,
    permissions: [PERMISSIONS.SHOPPER.SHOW],
    children: [
      {
        key: 'ai-distribution',
        title: 'sideBar.aiDistribution',
        path: '/dms-settings/ai-distribution',
        permissions: [PERMISSIONS.SHOPPER.SHOW],
      },
      {
        key: 'skills',
        title: 'sideBar.skills',
        path: '/dms-settings/skills',
        permissions: ['manage_skill_view'],
      },
      {
        key: 'shoppers',
        title: 'sideBar.shoppers',
        path: '/dms-settings/shoppers',
        permissions: [PERMISSIONS.SHOPPER.SHOW],
      },
      {
        key: 'shopper-order-status',
        title: 'sideBar.shopperOrderStatus',
        path: '/dms-settings/shopper-order-status',
        permissions: [PERMISSIONS.SHOPPER.SHOW],
      },
      {
        key: 'service-types',
        title: 'sideBar.serviceTypes',
        path: '/dms-settings/service-types',
        permissions: ['manage_service_type_view'],
      },
      {
        key: 'trip-types',
        title: 'sideBar.tripTypes',
        path: '/dms-settings/trip-types',
        permissions: ['manage_trip_type_view'],
      },
      {
        key: 'custody',
        title: 'sideBar.custody',
        path: '/dms-settings/custody',
        permissions: ['shoper_custody_view'],
      },
    ],
  },
  {
    key: 'purchasing',
    title: 'sideBar.purchasing',
    icon: PurchasingIcon,
    children: [
      {
        key: 'predict-products',
        title: 'sideBar.predictProducts',
        path: '/purchasing/predict-products',
        permissions: [PERMISSIONS.PURCHASING.PREDICT_PRODUCTS.VIEW],
      },
      {
        key: 'purchasing-requests',
        title: 'sideBar.purchasingRequests',
        path: '/purchasing/requests',
        permissions: [PERMISSIONS.PURCHASING.REQUESTS.VIEW],
      },
      {
        key: 'all-purchasing-requests',
        title: 'sideBar.allPurchasingRequests',
        path: '/purchasing/all-requests',
        permissions: [PERMISSIONS.PURCHASING.ALL_REQUESTS.VIEW],
      },
    ],
  },
  {
    key: 'dms-reports',
    title: 'sideBar.dmsReports',
    icon: DMSSettingsIcon,
    children: [
      {
        key: 'dms-dashboard',
        title: 'sideBar.dmsDashboard',
        path: '/dms-reports/dashboard',
        permissions: [PERMISSIONS.DMS.DRIVER_TRACKER.VIEW],
      },
      {
        key: 'top-10-shoppers',
        title: 'sideBar.top10Shoppers',
        path: '/dms-reports/top-10-shoppers',
        permissions: [PERMISSIONS.DMS.DRIVER_TRACKER.VIEW],
      },
      {
        key: 'driver-tracker',
        title: 'sideBar.driverTracker',
        path: '/dms/driver-tracker',
        permissions: [PERMISSIONS.DMS.DRIVER_TRACKER.VIEW],
      },
      {
        key: 'agent-kpi',
        title: 'sideBar.agentKpi',
        path: '/dms/agent_kpi',
        permissions: [PERMISSIONS.DMS.AGENT_KPI.VIEW],
      },
      {
        key: 'prediction',
        title: 'sideBar.prediction',
        path: '/dms/prediction',
        permissions: [PERMISSIONS.DMS.PREDICTION.VIEW],
      },
    ],
  },
];

const itemHasPermissionAccess = (
  item: MenuItem,
  hasAnyPermission: (permissions: string[]) => boolean,
  hasAllPermissions?: (permissions: string[]) => boolean
): boolean => {
  if (!item.permissions || item.permissions.length === 0) return true;

  // Fail closed if permission constants failed to resolve (e.g. [undefined])
  const validPermissions = item.permissions.filter(
    (permission): permission is string =>
      typeof permission === 'string' && permission.length > 0
  );
  if (validPermissions.length === 0) return false;

  if (item.requireAllPermissions) {
    return hasAllPermissions
      ? hasAllPermissions(validPermissions)
      : validPermissions.every((permission) => hasAnyPermission([permission]));
  }
  return hasAnyPermission(validPermissions);
};

// Helper function to filter menu items based on permissions
export const filterMenuByPermissions = (
  menuItems: MenuItem[],
  hasAnyPermission: (permissions: string[]) => boolean,
  hasAllPermissions?: (permissions: string[]) => boolean
): MenuItem[] => {
  return menuItems
    .map(item => {
      // Always enforce item-level permissions (including parent items).
      if (!itemHasPermissionAccess(item, hasAnyPermission, hasAllPermissions)) {
        return null;
      }

      // Filter children first if they exist
      if (item.children) {
        const filteredChildren = filterMenuByPermissions(
          item.children,
          hasAnyPermission,
          hasAllPermissions
        );
        
        if (filteredChildren.length === 0) {
          return null; // Hide parent if no children are visible
        }
        
        return {
          ...item,
          children: filteredChildren,
        };
      }

      // For items without children, check their own permissions
      return item;
    })
    .filter(Boolean) as MenuItem[];
};

// Helper function to check if a menu item should be visible
export const isMenuItemVisible = (
  item: MenuItem,
  hasAnyPermission: (permissions: string[]) => boolean,
  hasAllPermissions?: (permissions: string[]) => boolean
): boolean => {
  return itemHasPermissionAccess(item, hasAnyPermission, hasAllPermissions);
};

// Helper function to get menu item by path
export const getMenuItemByPath = (path: string): MenuItem | null => {
  const findItem = (items: MenuItem[]): MenuItem | null => {
    for (const item of items) {
      if (item.path === path) {
        return item;
      }
      if (item.children) {
        const found = findItem(item.children);
        if (found) return found;
      }
    }
    return null;
  };

  return findItem(MENU_CONFIG);
};

// Helper function to get all visible menu items
export const getVisibleMenuItems = (
  hasAnyPermission: (permissions: string[]) => boolean,
  company?: any,
  userType?: number,
  hasAllPermissions?: (permissions: string[]) => boolean
): MenuItem[] => {
  const filtered = filterMenuByPermissions(
    MENU_CONFIG,
    hasAnyPermission,
    hasAllPermissions
  );

  // Extract company product type (0 = Grocery, others = Pharmacy/Docs, etc.)
  const companyProductType =
    company?.main_settings?.basic_info?.company_product_type ??
    company?.products_type;

  // Filter out items based on company configuration (e.g. Salla, Insurance)
  return filtered.map(item => {
    // Filter out items based on allowed user types
    if (item.userTypes && item.userTypes.length > 0) {
      if (typeof userType !== 'number' || !item.userTypes.includes(userType)) {
        return null;
      }
    }

    // DMS Settings / Reports: hide when company lacks the dms model (not use_shopper)
    if (
      (item.key === 'dms-settings' || item.key === 'dms-reports') &&
      !hasCompanyModel(company, COMPANY_MODELS.DMS)
    ) {
      return null;
    }

    // Recursively filter children
    if (item.children) {
      const filteredChildren = item.children
        .map(child => {
          if (child.userTypes && child.userTypes.length > 0) {
            if (typeof userType !== 'number' || !child.userTypes.includes(userType)) {
              return null;
            }
          }
          if (child.key === 'customer-segmentation') {
            const hasCustomerSegmentationModel = company?.main_settings?.models?.some(
              (model: any) => model.title === 'customer_segmentation'
            );
            if (!hasCustomerSegmentationModel) {
              return null;
            }
          }
          if (child.key === 'sms-templates') {
            const hasSmsTemplateModel = company?.main_settings?.models?.some(
              (model: any) => model.title === 'sms_template'
            );
            if (!hasSmsTemplateModel) {
              return null;
            }
          }
          // Prescription / Rx review: hide when company lacks models.title=prescription
          if (
            child.key === 'pharmacistWorkspace' ||
            child.key === 'prescriptionStatuses' ||
            child.key === 'prescriptionRejectReasons'
          ) {
            if (!hasCompanyModel(company, COMPANY_MODELS.PRESCRIPTION)) {
              return null;
            }
          }
          if (child.key === 'orderFulfilment') {
            if (!hasCompanyModel(company, COMPANY_MODELS.ORDER_FULFILMENT)) {
              return null;
            }
          }
          // Refund workflow: hide when company lacks models.title=refund
          if (child.key === 'refunds' || child.key === 'refundApprovalRules') {
            if (!hasCompanyModel(company, COMPANY_MODELS.REFUND)) {
              return null;
            }
          }
          return child;
        })
        .filter(Boolean) as MenuItem[];
      
      if (filteredChildren.length === 0) {
        return null;
      }
      
      return {
        ...item,
        children: filteredChildren,
      };
    }
    
    return item;
  }).filter(Boolean) as MenuItem[];
}; 